import React from 'react';
import type { LodgementStatus } from '../../hooks/useLodgements';

/**
 * Shared presentation primitives for the Lodgements surfaces.
 *
 * The register, the per-person summary and the entry form all render the same
 * money, the same dates and the same status vocabulary. Keeping one copy here
 * is what stops the summary panel disagreeing with the row it summarises: a
 * second `money()` with a different rounding rule is exactly how a table stops
 * adding up.
 *
 * EMPTY IS A HYPHEN, and one hyphen. `-` (U+002D) only: em and en dashes are
 * banned repo-wide, and a mixed set of placeholder glyphs reads as three
 * different meanings when it is one.
 */

export const EMPTY = '-';

export const STATUS_OPTIONS: Array<{ value: LodgementStatus; label: string }> = [
  { value: 'NewLeads', label: 'New lead' },
  { value: 'Researching', label: 'Researching' },
  { value: 'PreApproved', label: 'Pre-approved' },
  { value: 'ConditionallyApproved', label: 'Conditionally approved' },
  { value: 'UnconditionallyApproved', label: 'Unconditionally approved' },
  { value: 'Lodged', label: 'Lodged' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'PendingSettlement', label: 'Pending settlement' },
  { value: 'Settled', label: 'Settled' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Cancelled', label: 'Cancelled' },
];

export const MIR_CATEGORIES = [
  'Docs/Income', 'Serviceability', 'Valuation', 'Policy',
  'Credit History', 'ID/Verification', 'Other',
];

export const STRUCTURES = ['Standard', 'Refinance', 'Bridging', 'Peak Debt', 'End Debt', 'Construction'];

const STATUS_STYLE: Record<string, string> = {
  Settled: 'bg-emerald-900/40 text-emerald-300',
  Withdrawn: 'bg-gray-700/50 text-gray-300',
  Cancelled: 'bg-red-900/40 text-red-300',
  PendingSettlement: 'bg-amber-900/40 text-amber-300',
  ConditionallyApproved: 'bg-amber-900/40 text-amber-300',
};

export const statusClass = (s: string): string => STATUS_STYLE[s] ?? 'bg-blue-900/40 text-blue-300';
export const labelFor = (s: string): string => STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;

/** Integer cents in, dollars out. Never a float: the repo-wide money rule. */
export const money = (cents: number | null | undefined): string =>
  cents == null ? EMPTY : `$${Math.round(cents / 100).toLocaleString('en-AU')}`;

export const moneyShort = (cents: number): string => {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}m`;
  if (d >= 1000) return `$${Math.round(d / 1000)}k`;
  return `$${Math.round(d)}`;
};

const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const [y, m, d] = iso.split('-');
  return `${d} ${MN[Number(m) - 1]} ${y.slice(2)}`;
};

/**
 * Target attainment colours. ONE scale, used by every "% of target" chip and
 * every progress bar on this feature, so a green on the gauge strip means the
 * same thing as a green in the summary panel two sections below it. Two scales
 * on one page is how a reader learns to distrust both.
 *
 * Breakpoints: 100 met, 70 within reach, below that behind.
 */
export const ATTAINMENT_MET = 100;
export const ATTAINMENT_CLOSE = 70;

export const attainmentChip = (percent: number): string =>
  percent >= ATTAINMENT_MET
    ? 'bg-emerald-900/40 text-emerald-300'
    : percent >= ATTAINMENT_CLOSE
      ? 'bg-amber-900/40 text-amber-300'
      : 'bg-red-900/40 text-red-300';

/** The bar fill matching `attainmentChip`, on the same breakpoints. */
export const attainmentBar = (percent: number): string =>
  percent >= ATTAINMENT_MET
    ? 'bg-emerald-500'
    : percent >= ATTAINMENT_CLOSE
      ? 'bg-amber-500'
      : 'bg-red-500';

/**
 * The same three states as literal hex, for Recharts.
 *
 * SVG fills cannot take a Tailwind class, so a chart has to name the colour
 * directly. These are the hex values behind emerald-500 / amber-500 / red-500,
 * kept beside the class helpers so a green bar in a chart and a green chip in a
 * table stay the same green and, more importantly, keep meaning the same thing.
 */
export const attainmentHex = (percent: number): string =>
  percent >= ATTAINMENT_MET
    ? '#10B981'
    : percent >= ATTAINMENT_CLOSE
      ? '#F59E0B'
      : '#EF4444';

/**
 * Chart palette, matching the app's existing Recharts components
 * (`LenderBarChart`, `MonthlyBreakdownChart`).
 */
export const CHART_BLUE = '#3B82F6';
export const CHART_EMERALD = '#10B981';
export const CHART_AMBER = '#F59E0B';
export const CHART_VIOLET = '#8B5CF6';
export const CHART_GRID = '#344155';
export const CHART_AXIS = '#8892b0';

/**
 * Compact dollars for an axis tick. Takes DOLLARS, not cents.
 *
 * Chart data converts cents to dollars once, where the series is built, rather
 * than in every formatter that touches it. One divide at one boundary is the
 * only shape of this that cannot drift into a 100x error.
 */
export const dollarsCompact = (dollars: number): string => {
  if (!Number.isFinite(dollars)) return '$0';
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`;
  return `$${dollars.toFixed(0)}`;
};

/** Full dollars for a tooltip. Takes DOLLARS. */
export const dollarsFull = (dollars: number): string =>
  `$${Math.round(dollars).toLocaleString('en-AU')}`;

/**
 * Today on the VIEWER'S clock, as YYYY-MM-DD.
 *
 * `new Date().toISOString().slice(0, 10)` would be the UTC date, which for an
 * Australian user is yesterday for the first ten hours of every day. A target
 * whose effective date defaults to yesterday is not a cosmetic problem: it
 * decides which period the target starts judging.
 */
export const todayLocalIso = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export const inputCls =
  'w-full rounded-md border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600';

export function Field({
  label, children, required, hint, className,
}: {
  label: string; children: React.ReactNode; required?: boolean; hint?: string; className?: string;
}): React.ReactElement {
  return (
    <div className={className}>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[10px] text-gray-500">{hint}</p>}
    </div>
  );
}
